import { useMemo, useState } from "react";
import type {
  CraftableWeapon,
  CraftingComponent,
  CraftingSocketKey,
  MaterialDefinition,
  PlannerMode,
  WeaponConfiguration,
} from "@/lib/crafting/types";
import { SOCKET_LABELS } from "@/lib/crafting/types";
import { usePlanner } from "@/hooks/usePlanner";
import { findComponent } from "@/lib/crafting/engine";
import { assetUrl } from "@/lib/bungie/assets";
import { CraftingSlotBar } from "./CraftingSlotBar";
import { ComponentGrid } from "./ComponentGrid";
import { PerkTooltip } from "./PerkTooltip";
import { PlanSummaryPanel } from "./PlanSummaryPanel";
import { StatBlock } from "./StatBlock";
import { WeaponIdentityPanel, FlavorText } from "./WeaponIdentityPanel";
import { WeaponLevelDisplay } from "./WeaponLevelDisplay";
import { CraftingReviewScreen } from "./CraftingReviewScreen";

export interface CraftingScreenProps {
  weapon: CraftableWeapon;
  materials: Record<string, MaterialDefinition>;
  statNames?: Record<string, string>;
  mode?: PlannerMode;
  currentConfiguration?: WeaponConfiguration;
  /** Preselected plan, e.g. when opening a shared plan URL. */
  initialPlan?: WeaponConfiguration;
  currentWeaponLevel?: number;
  currentLevelPercent?: number;
  /** Rendered inside the header rather than as a separate band, which would
   *  cost a whole row of vertical space the 1080p layout does not have. */
  onBack?: () => void;
  notice?: string;
  /** Drives whether a shared link can carry the author's name. */
  session?: { signedIn: boolean; displayName?: string | null };
  /** The owned weapon being planned against, if any. Local use only. */
  ownedInstanceId?: string;
}

/**
 * The main crafting interface.
 *
 * Layout follows the reference captures: identity top-left, slot bar across the
 * middle, perk grid beneath it, tooltip to the right, level bottom-left, stats
 * bottom-right. The planning summary is an additional column that collapses
 * below the crafting area on narrow screens rather than compressing it.
 */
export function CraftingScreen({
  weapon,
  materials,
  statNames,
  mode = "new-shape",
  currentConfiguration,
  initialPlan,
  currentWeaponLevel,
  currentLevelPercent,
  onBack,
  notice,
  session,
  ownedInstanceId,
}: CraftingScreenProps) {
  const planner = usePlanner({
    weapon,
    mode,
    currentConfiguration,
    initialPlan,
    currentWeaponLevel,
    currentLevelPercent,
  });
  const { state, derived, select, reset } = planner;

  const [activeKey, setActiveKey] = useState<CraftingSocketKey>(
    () => weapon.sockets.find((s) => s.key !== "intrinsic")?.key ?? "intrinsic"
  );
  const [preview, setPreview] = useState<CraftingComponent | undefined>();
  const [reviewing, setReviewing] = useState(false);

  const activeSocket = weapon.sockets.find((s) => s.key === activeKey);
  const selectedInActive = findComponent(weapon, activeKey, state.plannedConfiguration[activeKey]);
  // Hovering previews; otherwise show what is actually selected in this column.
  const tooltipComponent = preview ?? selectedInActive;
  const previewing = Boolean(preview) && preview?.plugHash !== selectedInActive?.plugHash;

  const backdrop = assetUrl(weapon.screenshot);
  // Only a real swap is worth previewing — hovering the component already in the
  // slot would draw a zero-width delta on every bar.
  const statPreview = useMemo(
    () =>
      preview && preview.plugHash !== selectedInActive?.plugHash
        ? { key: activeKey, component: preview }
        : undefined,
    [preview, selectedInActive, activeKey]
  );

  if (reviewing) {
    return (
      <CraftingReviewScreen
        weapon={weapon}
        planner={planner}
        materials={materials}
        session={session}
        ownedInstanceId={ownedInstanceId}
        onBack={() => setReviewing(false)}
      />
    );
  }

  return (
    <div className="relative min-h-full">
      {/* Blurred weapon art, exactly as the game frames the crafting screen. */}
      {backdrop && (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-10 bg-cover bg-center opacity-25 blur-sm"
          style={{ backgroundImage: `url(${backdrop})` }}
        />
      )}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 bg-void/75" />

      {/* Vertical rhythm is deliberately tight: the whole interface is meant to
          fit a 1080p viewport without scrolling. */}
      <div className="mx-auto flex max-w-[110rem] flex-col gap-4 px-4 py-4 sm:px-8 sm:py-5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="label-caps -mb-2 self-start text-xs text-ink-2 transition-colors hover:text-ink"
          >
            ← All weapons
          </button>
        )}

        {notice && (
          <p
            role="status"
            className="border border-enhanced/40 bg-enhanced/10 px-3 py-2 text-sm text-enhanced"
          >
            {notice}
          </p>
        )}

        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <WeaponIdentityPanel weapon={weapon} mode={state.mode} />
            <FlavorText text={weapon.flavorText} />
          </div>
          {derived.isDirty && state.mode === "owned-reshape" && (
            <button
              type="button"
              onClick={reset}
              className="label-caps border border-line-dim px-3 py-2 text-xs transition-colors hover:bg-white/10"
            >
              Reset Preview
            </button>
          )}
        </header>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0 space-y-4">
            <div className="corner-brackets border border-line-dim">
              <CraftingSlotBar
                weapon={weapon}
                planned={state.plannedConfiguration}
                current={state.currentConfiguration}
                activeKey={activeKey}
                currentLevel={state.currentWeaponLevel}
                onActivate={(key) => {
                  setActiveKey(key);
                  setPreview(undefined);
                }}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
              <div className="min-w-0">
                <h2 className="label-caps mb-2 text-sm text-ink-2">
                  {SOCKET_LABELS[activeKey]}
                  {activeSocket && (
                    <span className="ml-2 normal-case tracking-normal text-ink-3">
                      {activeSocket.options.length} options
                    </span>
                  )}
                </h2>

                {activeSocket ? (
                  <ComponentGrid
                    socket={activeSocket}
                    materials={materials}
                    selectedHash={state.plannedConfiguration[activeKey]}
                    equippedHash={state.currentConfiguration?.[activeKey]}
                    currentLevel={state.currentWeaponLevel}
                    onSelect={(plugHash) => select(activeKey, plugHash)}
                    onPreview={setPreview}
                  />
                ) : (
                  <p className="text-sm text-ink-3">
                    This weapon has no craftable options in that column.
                  </p>
                )}
              </div>

              {/* Fixed height, not min-height: perk descriptions vary a lot in
                  length, so anything elastic reflowed the page on every hover.
                  A rare over-long description scrolls inside the panel. */}
              <div className="lg:h-[21rem] lg:w-88 lg:overflow-y-auto">
                {tooltipComponent ? (
                  <PerkTooltip
                    component={tooltipComponent}
                    materials={materials}
                    statNames={statNames}
                    currentLevel={state.currentWeaponLevel}
                    state={previewing ? "preview" : "selected"}
                    equipped={
                      state.currentConfiguration?.[activeKey] === tooltipComponent.plugHash
                    }
                  />
                ) : (
                  <div className="border border-line-dim bg-panel/80 px-4 py-6">
                    <h3 className="label-caps text-base font-semibold">
                      Empty {SOCKET_LABELS[activeKey]} socket
                    </h3>
                    <p className="mt-2 text-sm text-ink-2">
                      No component currently selected. More options become available as the weapon
                      level increases.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-6 border-t border-line-dim pt-3">
              <WeaponLevelDisplay
                currentLevel={state.currentWeaponLevel}
                currentPercent={state.currentLevelPercent}
                requiredLevel={derived.requiredWeaponLevel}
                fromAccount={state.mode === "owned-reshape"}
              />
              <div className="ml-auto">
                <StatBlock weapon={weapon} selected={derived.selected} preview={statPreview} />
              </div>
            </div>
          </div>

          <PlanSummaryPanel
            planner={planner}
            materials={materials}
            onReview={() => setReviewing(true)}
          />
        </div>
      </div>
    </div>
  );
}
